import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";

export interface HexacoSamplePdfPreview {
  title: string;
  url: string;
}

interface HexacoSamplePdfModalProps {
  preview: HexacoSamplePdfPreview | null;
  onClose: () => void;
}

const HexacoSamplePdfModal: React.FC<HexacoSamplePdfModalProps> = ({
  preview,
  onClose,
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (preview) {
      setIsLoading(true);
      setHasError(false);
    }
  }, [preview?.url]);

  return (
    <Modal
      isOpen={!!preview}
      onClose={onClose}
      size="full"
      motionPreset="slideInBottom"
    >
      <ModalOverlay bg="blackAlpha.800" />
      <ModalContent
        m={0}
        borderRadius={0}
        maxW="100vw"
        h="100dvh"
        bg="gray.100"
      >
        <ModalHeader
          pr={14}
          py={3}
          fontSize="md"
          borderBottomWidth="1px"
          borderColor="gray.200"
          bg="white"
        >
          {preview?.title}
        </ModalHeader>
        <ModalCloseButton size="lg" top={2} />
        <ModalBody p={0} flex={1} position="relative" overflow="hidden">
          {isLoading && !hasError && (
            <Flex
              position="absolute"
              inset={0}
              align="center"
              justify="center"
              bg="gray.50"
              zIndex={1}
            >
              <Spinner size="xl" color="teal.400" thickness="3px" />
            </Flex>
          )}
          {hasError ? (
            <Flex
              align="center"
              justify="center"
              h="full"
              px={6}
              direction="column"
              gap={3}
            >
              <Text color="gray.600" textAlign="center">
                {t("hexaco.purchase.samples.loadError")}
              </Text>
            </Flex>
          ) : (
            preview && (
              <Box
                as="iframe"
                src={preview.url}
                title={preview.title}
                w="full"
                h="full"
                border="none"
                display="block"
                onLoad={() => setIsLoading(false)}
                onError={() => {
                  setIsLoading(false);
                  setHasError(true);
                }}
              />
            )
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default HexacoSamplePdfModal;
